import React, { useState, useEffect, useRef } from "react";
import { AlertCircle, CheckCircle2, AlertTriangle, Info, X } from "lucide-react";

export type ToastType = "error" | "success" | "warning" | "info";

export interface ToastOptions {
  id?: string;
  title?: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export interface ToastItem {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  duration: number;
  action?: {
    label: string;
    onClick: () => void;
  };
  createdAt: number;
}

// ---------------------------------------------------------------------------
// Observable Toast Store
// ---------------------------------------------------------------------------
type ToastListener = (toasts: ToastItem[]) => void;

let toasts: ToastItem[] = [];
const listeners = new Set<ToastListener>();

const emitChange = () => {
  listeners.forEach((listener) => listener([...toasts]));
};

const removeToast = (id: string) => {
  toasts = toasts.filter((t) => t.id !== id);
  emitChange();
};

const addToast = (
  type: ToastType,
  message: string,
  options?: ToastOptions
): string => {
  const id = options?.id || `toast_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
  const defaultDuration = type === "error" ? 6000 : 4000;
  const duration = options?.duration !== undefined ? options?.duration : defaultDuration;

  const newToast: ToastItem = {
    id,
    type,
    title: options?.title,
    message,
    duration,
    action: options?.action,
    createdAt: Date.now(),
  };

  // Keep a maximum of 5 toasts simultaneously
  toasts = [newToast, ...toasts.filter((t) => t.id !== id)].slice(0, 5);
  emitChange();

  return id;
};

// ---------------------------------------------------------------------------
// Public Toast API
// ---------------------------------------------------------------------------
export const toast = {
  error: (message: string, options?: ToastOptions) =>
    addToast("error", message, { title: options?.title || "Error", ...options }),
  success: (message: string, options?: ToastOptions) =>
    addToast("success", message, { title: options?.title || "Success", ...options }),
  warning: (message: string, options?: ToastOptions) =>
    addToast("warning", message, { title: options?.title || "Warning", ...options }),
  info: (message: string, options?: ToastOptions) =>
    addToast("info", message, { title: options?.title || "Information", ...options }),
  dismiss: (id: string) => removeToast(id),
  clear: () => {
    toasts = [];
    emitChange();
  },
};

// ---------------------------------------------------------------------------
// React Hook for local subscriptions
// ---------------------------------------------------------------------------
export function useToast() {
  const [activeToasts, setActiveToasts] = useState<ToastItem[]>(toasts);

  useEffect(() => {
    const handleChange = (newToasts: ToastItem[]) => {
      setActiveToasts(newToasts);
    };
    listeners.add(handleChange);
    return () => {
      listeners.delete(handleChange);
    };
  }, []);

  return {
    toasts: activeToasts,
    toast,
    dismiss: toast.dismiss,
  };
}

// ---------------------------------------------------------------------------
// Single Toast Notification Card
// ---------------------------------------------------------------------------
function ToastCard({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(100);
  const startTimeRef = useRef(Date.now());
  const remainingTimeRef = useRef(item.duration);

  useEffect(() => {
    if (item.duration <= 0) return;

    let animFrame: number;
    const updateProgress = () => {
      if (!isPaused) {
        const elapsed = Date.now() - startTimeRef.current;
        const currentRemaining = Math.max(0, remainingTimeRef.current - elapsed);
        const percent = (currentRemaining / item.duration) * 100;
        setProgress(percent);

        if (currentRemaining <= 0) {
          onDismiss();
          return;
        }
      }
      animFrame = requestAnimationFrame(updateProgress);
    };

    animFrame = requestAnimationFrame(updateProgress);
    return () => cancelAnimationFrame(animFrame);
  }, [isPaused, item.duration, onDismiss]);

  const handleMouseEnter = () => {
    if (item.duration > 0) {
      remainingTimeRef.current = Math.max(
        0,
        remainingTimeRef.current - (Date.now() - startTimeRef.current)
      );
      setIsPaused(true);
    }
  };

  const handleMouseLeave = () => {
    if (item.duration > 0) {
      startTimeRef.current = Date.now();
      setIsPaused(false);
    }
  };

  const styleConfig = {
    error: {
      cardBg: "bg-[#161114]/95 border-rose-500/30 shadow-[0_12px_40px_-8px_rgba(244,63,94,0.3)]",
      iconBg: "bg-rose-500/15 text-rose-400 border border-rose-500/30",
      titleColor: "text-rose-200",
      textColor: "text-rose-100/90",
      progressBar: "bg-rose-500",
      icon: AlertCircle,
    },
    success: {
      cardBg: "bg-[#0e1713]/95 border-emerald-500/30 shadow-[0_12px_40px_-8px_rgba(16,185,129,0.3)]",
      iconBg: "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30",
      titleColor: "text-emerald-200",
      textColor: "text-emerald-100/90",
      progressBar: "bg-emerald-500",
      icon: CheckCircle2,
    },
    warning: {
      cardBg: "bg-[#18140c]/95 border-amber-500/30 shadow-[0_12px_40px_-8px_rgba(245,158,11,0.3)]",
      iconBg: "bg-amber-500/15 text-amber-400 border border-amber-500/30",
      titleColor: "text-amber-200",
      textColor: "text-amber-100/90",
      progressBar: "bg-amber-500",
      icon: AlertTriangle,
    },
    info: {
      cardBg: "bg-[#0c141a]/95 border-cyan-500/30 shadow-[0_12px_40px_-8px_rgba(6,182,212,0.3)]",
      iconBg: "bg-cyan-500/15 text-cyan-400 border border-cyan-500/30",
      titleColor: "text-cyan-200",
      textColor: "text-cyan-100/90",
      progressBar: "bg-cyan-500",
      icon: Info,
    },
  }[item.type];

  const Icon = styleConfig.icon;

  return (
    <div
      role="alert"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`relative w-full max-w-md overflow-hidden rounded-2xl border p-4 backdrop-blur-xl transition-all duration-300 pointer-events-auto transform translate-y-0 opacity-100 ${styleConfig.cardBg}`}
    >
      <div className="flex items-start gap-3.5">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${styleConfig.iconBg}`}>
          <Icon className="h-5 w-5" />
        </div>

        <div className="flex-1 pt-0.5">
          {item.title && (
            <h4 className={`text-xs font-bold tracking-wide uppercase ${styleConfig.titleColor}`}>
              {item.title}
            </h4>
          )}
          <p className={`mt-0.5 text-xs font-medium leading-relaxed break-words ${styleConfig.textColor}`}>
            {item.message}
          </p>

          {item.action && (
            <div className="mt-2.5">
              <button
                type="button"
                onClick={() => {
                  item.action?.onClick();
                  onDismiss();
                }}
                className="rounded-lg bg-white/10 px-3 py-1 text-[11px] font-semibold text-white hover:bg-white/20 transition"
              >
                {item.action.label}
              </button>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss notification"
          className="rounded-lg p-1 text-slate-400 hover:bg-white/10 hover:text-white transition shrink-0 -mr-1 -mt-1"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {item.duration > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/5 overflow-hidden">
          <div
            className={`h-full transition-all duration-75 ${styleConfig.progressBar}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Global Toast Container (Mounted in App)
// ---------------------------------------------------------------------------
export function ToastContainer() {
  const { toasts, dismiss } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div
      aria-live="assertive"
      className="fixed top-4 right-4 z-[99999] flex flex-col gap-2.5 max-w-[calc(100vw-2rem)] sm:max-w-md pointer-events-none"
    >
      {toasts.map((item) => (
        <ToastCard key={item.id} item={item} onDismiss={() => dismiss(item.id)} />
      ))}
    </div>
  );
}
